// AnimatedCounter.tsx
"use client";

import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import { cn } from "@/lib/utils";
import React from "react";

interface AnimatedCounterProps {
  initialCount: number;
  className?: string;
  delay?: number; // milliseconds
  testMode?: boolean; // When true, uses shorter delay for testing
}

export function AnimatedCounter({
  initialCount,
  className,
  delay = 30000, // Default 30 seconds
  testMode = false,
}: AnimatedCounterProps) {
  const [count, setCount] = useState<number>(initialCount);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [showPlusOne, setShowPlusOne] = useState<boolean>(false);

  useEffect(() => {
    // Use shorter delay in test mode for easier development
    const actualDelay = testMode ? 5000 : delay;

    const timer = setTimeout(() => {
      setIsAnimating(true);
      setShowPlusOne(true);

      // Update count after a slight delay for better animation effect
      setTimeout(() => {
        setCount((prevCount) => prevCount + 1);
      }, 600);

      // Hide the +1 indicator after animation completes
      setTimeout(() => {
        setShowPlusOne(false);
      }, 1500);

      // Reset animation states
      setTimeout(() => {
        setIsAnimating(false);
      }, 1600);
    }, actualDelay);

    return () => clearTimeout(timer);
  }, [delay, testMode]);

  return (
    <div className={cn("flex items-center gap-3", className)}>
      <div className="relative inline-flex items-center justify-center">
        <Heart
          className={cn(
            "text-vibrantOrange h-6 w-6 transition-all",
            isAnimating ? "animate-heartbeat" : ""
          )}
          fill={isAnimating ? "currentColor" : "none"}
        />
      </div>

      <div className="relative">
        {showPlusOne && (
          <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-vibrantOrange text-white rounded px-2 py-0.5 text-xs font-medium animate-fadeUp">
            +1
          </div>
        )}
        <p className="text-pureWhite/90">
          <span
            className={cn(
              "text-vibrantOrange font-semibold mr-2",
              isAnimating ? "animate-counterChange" : ""
            )}
          >
            {count}
          </span>{" "}
          företag startade sin testperiod senaste veckan
        </p>
      </div>
    </div>
  );
}

export default AnimatedCounter;
